import type { Node } from '@xyflow/react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Trash2, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { WorkflowPropertiesFormBody, validateNodeData } from './WorkflowPropertiesPanel';
import type { WorkflowNodeData } from './types';

export interface WorkflowPropertiesDialogProps {
  selectedNode: Node | null;
  onUpdateNode?: (nodeId: string, data: Partial<WorkflowNodeData>) => void;
  onDeleteNode?: (nodeId: string) => void;
  onClose: () => void;
}

export function WorkflowPropertiesDialog({
  selectedNode,
  onUpdateNode,
  onDeleteNode,
  onClose,
}: WorkflowPropertiesDialogProps) {
  const { toast } = useToast();
  const open = !!selectedNode;

  const handleDelete = () => {
    if (selectedNode && onDeleteNode) {
      onDeleteNode(selectedNode.id);
      onClose();
    }
  };

  const handleValidate = () => {
    if (!selectedNode) return;
    const data = selectedNode.data as WorkflowNodeData;
    const { valid, missing } = validateNodeData(data);
    if (valid) {
      onClose();
      toast({ title: 'Updated', description: 'Step properties have been saved.' });
    } else {
      toast({
        variant: 'destructive',
        title: 'Missing required fields',
        description: `Fill in: ${missing.join(', ')}`,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        {selectedNode && (
          <>
            <DialogHeader>
              <DialogTitle>Step properties</DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-2">
              <WorkflowPropertiesFormBody selectedNode={selectedNode} onUpdateNode={onUpdateNode} />
            </ScrollArea>
            <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
              <div className="flex w-full justify-end gap-2 sm:w-auto sm:order-2">
                <Button type="button" size="sm" onClick={handleValidate} className="gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Validate
                </Button>
              </div>
              {onDeleteNode && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="w-full gap-1.5 sm:order-1 sm:w-auto"
                  onClick={handleDelete}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove step
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
